import * as React from "react";
import {Box, PieChart} from "@amzn/awsui-components-react";
import { UATProject, UATStatus } from "../../types/UATProject";
import { useMemo } from "react";

interface TestRunsViewProps {
  project: UATProject;
}

export default function CurrentTestStatusView({ project }: TestRunsViewProps) {
  // Early return if project is not loaded
  if (!project) {
    return (
        <Box textAlign="center" color="inherit">
          <b>Loading...</b>
          <Box variant="p" color="inherit">
            Loading current test status data...
          </Box>
        </Box>
    );
  }

  const pieChartData = useMemo(() => {
    // Check if project has test cases
    if (!project.testCases || project.testCases.length === 0) {
      return [];
    }

    // Count test cases by status
    const statusCounts = project.testCases.reduce((acc, testCase) => {
      const status = testCase.status;
      const statusName = UATStatus[status] || 'Unknown';
      
      if (!acc[statusName]) {
        acc[statusName] = 0;
      }
      acc[statusName]++;
      
      return acc;
    }, {} as Record<string, number>);

    // Convert to pie chart data format
    return Object.entries(statusCounts).map(([statusName, count]) => ({
      title: statusName,
      value: count,
      lastUpdate: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    }));
  }, [project.testCases]);

  // Show empty state if no test cases
  if (pieChartData.length === 0) {
    return (
      <Box textAlign="center" color="inherit">
        <b>No test cases available</b>
        <Box variant="p" color="inherit">
          This project doesn't have any test cases to display status for.
        </Box>
      </Box>
    );
  }
  
  return (
      <PieChart
          data={pieChartData}
          detailPopoverContent={(datum, sum) => [
            { key: "Test case count", value: datum.value },
            {
              key: "Percentage",
              value: `${((datum.value / sum) * 100).toFixed(1)}%`
            },
            { key: "Last updated", value: datum.lastUpdate }
          ]}
          segmentDescription={(datum, sum) =>
              `${datum.value} test case${datum.value !== 1 ? 's' : ''}, ${(
                  (datum.value / sum) * 100
              ).toFixed(1)}%`
          }
          ariaDescription="Pie chart showing the current status distribution of test cases in this UAT project."
          ariaLabel="Test case status distribution"
          hideFilter
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data available</b>
              <Box variant="p" color="inherit">
                There are no test cases with status data available.
              </Box>
            </Box>
          }
      />
  );
}
